interface FormState {
  supplierName: string;
  supplierSku: string;
  supplierProductName: string;
  ean: string;
  upc: string;
  dun14: string;
  gtin: string;
  unitPrice: string;
  casePrice: string;
  currency: string;
  leadTimeDays: string;
  minOrderQuantity: string;
  orderMultiple: string;
  maxOrderQuantity: string;
  packagingUnitSize: string;
  packagingUnitOfMeasure: string;
  packagingUnitsPerCase: string;
  packagingCasesPerPallet: string;
  packagingDescription: string;
  isPreferred: boolean;
  notes: string;
}

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];
const UNIT_OPTIONS = ['kg', 'g', 'lb', 'oz', 'L', 'ml', 'ea', 'case', 'pack'];

interface SectionProps {
  readonly form: FormState;
  readonly updateField: (field: keyof FormState, value: string | boolean) => void;
}

export function BasicInfoSection({
  form,
  updateField,
}: SectionProps) {
  return (
    <div className="space-y-4">
      <FormField label="Supplier Name" required>
        <input
          type="text"
          value={form.supplierName}
          onChange={(e) => updateField('supplierName', e.target.value)}
          placeholder="e.g., Ocean Fresh Seafood Co."
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </FormField>

      <FormField label="Supplier Product Name">
        <input
          type="text"
          value={form.supplierProductName}
          onChange={(e) => updateField('supplierProductName', e.target.value)}
          placeholder="How the supplier names this product"
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </FormField>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="isPreferred"
          checked={form.isPreferred}
          onChange={(e) => updateField('isPreferred', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <label htmlFor="isPreferred" className="text-sm text-gray-700">
          Mark as preferred supplier
        </label>
      </div>

      <FormField label="Notes">
        <textarea
          value={form.notes}
          onChange={(e) => updateField('notes', e.target.value)}
          placeholder="Any additional notes about this supplier..."
          rows={3}
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </FormField>
    </div>
  );
}

export function ProductCodesSection({
  form,
  updateField,
}: SectionProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 mb-4">
        Enter the product codes used by this supplier. These may differ from your internal codes.
      </p>

      <FormField label="Supplier SKU">
        <input
          type="text"
          value={form.supplierSku}
          onChange={(e) => updateField('supplierSku', e.target.value)}
          placeholder="Supplier's product code"
          className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </FormField>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="EAN (European Article Number)">
          <input
            type="text"
            value={form.ean}
            onChange={(e) => updateField('ean', e.target.value)}
            placeholder="13-digit barcode"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="UPC (Universal Product Code)">
          <input
            type="text"
            value={form.upc}
            onChange={(e) => updateField('upc', e.target.value)}
            placeholder="12-digit barcode"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="DUN-14 (Shipping Container Code)">
          <input
            type="text"
            value={form.dun14}
            onChange={(e) => updateField('dun14', e.target.value)}
            placeholder="14-digit case code"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="GTIN (Global Trade Item Number)">
          <input
            type="text"
            value={form.gtin}
            onChange={(e) => updateField('gtin', e.target.value)}
            placeholder="8, 12, 13, or 14 digits"
            className="w-full rounded-md border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>
      </div>
    </div>
  );
}

export function PackagingSection({
  form,
  updateField,
}: SectionProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 mb-4">
        Define how this supplier packages the product (e.g., 1kg × 12 per case).
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="Unit Size">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.packagingUnitSize}
            onChange={(e) => updateField('packagingUnitSize', e.target.value)}
            placeholder="e.g., 1"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="Unit of Measure">
          <select
            value={form.packagingUnitOfMeasure}
            onChange={(e) => updateField('packagingUnitOfMeasure', e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select unit...</option>
            {UNIT_OPTIONS.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Units per Case">
          <input
            type="number"
            min="1"
            value={form.packagingUnitsPerCase}
            onChange={(e) => updateField('packagingUnitsPerCase', e.target.value)}
            placeholder="e.g., 12"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="Cases per Pallet">
          <input
            type="number"
            min="1"
            value={form.packagingCasesPerPallet}
            onChange={(e) => updateField('packagingCasesPerPallet', e.target.value)}
            placeholder="e.g., 48"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>
      </div>

      <FormField label="Packaging Description">
        <input
          type="text"
          value={form.packagingDescription}
          onChange={(e) => updateField('packagingDescription', e.target.value)}
          placeholder="e.g., Vacuum-sealed portions in cardboard case"
          className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </FormField>
    </div>
  );
}

export function PricingSection({
  form,
  updateField,
}: SectionProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <FormField label="Currency" required>
          <select
            value={form.currency}
            onChange={(e) => updateField('currency', e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {CURRENCY_OPTIONS.map((curr) => (
              <option key={curr} value={curr}>{curr}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Unit Price" required>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.unitPrice}
            onChange={(e) => updateField('unitPrice', e.target.value)}
            placeholder="0.00"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>

        <FormField label="Case Price">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.casePrice}
            onChange={(e) => updateField('casePrice', e.target.value)}
            placeholder="0.00"
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </FormField>
      </div>

      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium text-gray-900 mb-4">Order Constraints</h4>
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Lead Time (days)" required>
            <input
              type="number"
              min="0"
              value={form.leadTimeDays}
              onChange={(e) => updateField('leadTimeDays', e.target.value)}
              placeholder="e.g., 3"
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>

          <FormField label="Minimum Order Quantity" required>
            <input
              type="number"
              min="1"
              value={form.minOrderQuantity}
              onChange={(e) => updateField('minOrderQuantity', e.target.value)}
              placeholder="e.g., 1"
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>

          <FormField label="Order Multiple">
            <input
              type="number"
              min="1"
              value={form.orderMultiple}
              onChange={(e) => updateField('orderMultiple', e.target.value)}
              placeholder="e.g., 6 (must order in multiples)"
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>

          <FormField label="Maximum Order Quantity">
            <input
              type="number"
              min="1"
              value={form.maxOrderQuantity}
              onChange={(e) => updateField('maxOrderQuantity', e.target.value)}
              placeholder="Leave empty for no limit"
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
}

interface FormFieldProps {
  readonly label: string;
  readonly required?: boolean;
  readonly children: React.ReactNode;
}

function FormField({
  label,
  required,
  children,
}: FormFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

export type { FormState };
